import { Injectable } from '@angular/core';
import { Observable, of, from, map, catchError, switchMap } from 'rxjs';
import {
	Export,
	GameData,
	PluginType,
	AIMessage,
	RendererGameDefinitionService,
	RendererGameDefinition,
} from '@meta-press/shared-plugins';
import { AiService } from '../services/ai/ai.service';
import { NotificationService } from '../notification-service/notification.service';
import { AIAPIResponse } from '../model/AIAPIResponse';

export interface MigrationResult {
	success: boolean;
	migratedExport?: Export;
	error?: string;
}

@Injectable({
	providedIn: 'root',
})
export class MigrationService {
	constructor(
		private aiService: AiService,
		private notificationService: NotificationService,
	) {
		this.rendererGameDefinitionService =
			new RendererGameDefinitionService();
	}

	private rendererGameDefinitionService: RendererGameDefinitionService;

	/**
	 * Check if an export can be successfully parsed with current GameData structure
	 */
	public checkExportCompatibility(exportData: Export): Observable<boolean> {
		return from(this.tryParseExport(exportData)).pipe(
			map(() => true),
			catchError(() => of(false)),
		);
	}

	/**
	 * Check export compatibility and return detailed error information
	 */
	public checkExportCompatibilityWithError(
		exportData: Export,
	): Observable<{ isCompatible: boolean; error?: string }> {
		return from(this.tryParseExport(exportData)).pipe(
			map(() => ({ isCompatible: true })),
			catchError((error) => {
				const errorMessage =
					error?.message ||
					error?.toString() ||
					'Unknown compatibility error';
				return of({ isCompatible: false, error: errorMessage });
			}),
		);
	}

	/**
	 * Try to parse an export and ensure GameData has proper methods
	 */
	private async tryParseExport(exportData: Export): Promise<void> {
		if (!exportData.gameData) {
			throw new Error('No game data found in export');
		}

		const gameType = exportData.gameData.type;

		const gameDefinition =
			await this.rendererGameDefinitionService.getGameDefinition(
				gameType,
			);

		if (!gameDefinition) {
			throw new Error(`Game definition not found for type: ${gameType}`);
		}

		// Try to add methods - this will fail if structure has changed significantly
		try {
			const testCopy = GameData.deepClone(exportData.gameData);

			// Get available types and build the map with actual constructors
			const availableTypes =
				this.rendererGameDefinitionService.AvailablePluginTypes;

			// Build the GameData types map by loading constructors for all available types
			const gameDataTypes = new Map<PluginType, new () => GameData>();

			// Load constructors for all available types
			for (const pluginType of availableTypes) {
				try {
					const constructor =
						await this.rendererGameDefinitionService.getGameDataConstructor(
							pluginType,
						);
					if (constructor) {
						gameDataTypes.set(pluginType, constructor);
					}
				} catch (error) {
					// Silently skip types that fail to load
				}
			}

			// First, add methods to the main GameData object so we can use built-in traversal
			GameData.addMethods(testCopy, gameDefinition.gameDataType);

			// Now use GameData's own method to collect all GameData instances
			const allGameDataObjects = testCopy.collectAllGameDataInstances();

			// Use GameData's built-in method to add prototypes to all nested objects
			testCopy.addPrototypesToAllGameData(gameDataTypes);

			// Try to call a method to ensure it works
			const usedArticles = testCopy.GetUsedArticles();

			// Also test nested objects if they exist
			const containedObjects =
				testCopy.GetContainedLocalGameDataObjects();

			containedObjects.forEach((obj: GameData, index: number) => {
				try {
					const nestedArticles = obj.GetUsedArticles(); // This will throw if methods are missing
				} catch (nestedError) {
					throw nestedError;
				}
			});
		} catch (error) {
			throw new Error(`Failed to parse game data: ${error}`);
		}
	}

	/**
	 * Extract GameDataType from an unparsed export JSON
	 */
	public parseExportGameDataType(exportJson: any): PluginType {
		try {
			if (
				exportJson.gameData &&
				typeof exportJson.gameData.type === 'number'
			) {
				return exportJson.gameData.type as PluginType;
			}
			throw new Error('Could not find game data type in export');
		} catch (error) {
			return PluginType.None;
		}
	}

	/**
	 * Get the TypeScript structure of a GameData type as a string
	 * Uses the dynamic getStructureDefinition method from each GameData class
	 */
	public async getGameDataStructure(pluginType: PluginType): Promise<string> {
		const gameDefinition =
			await this.rendererGameDefinitionService.getGameDefinition(
				pluginType,
			);

		if (!gameDefinition) {
			throw new Error(
				`Game definition not found for type: ${pluginType}`,
			);
		}

		// Create an instance of the GameData class and get its structure definition
		const gameDataInstance = new gameDefinition.gameDataType();

		// Use the dynamic structure definition method
		try {
			const structureDefinition =
				gameDataInstance.getStructureDefinition();
			return structureDefinition;
		} catch (error) {
			// Fallback to generic definition - this should rarely be used now
			const className = gameDataInstance.constructor.name;
			return this.getGameDataTypeDefinition(pluginType, className);
		}
	}

	/**
	 * Migrate old GameData to new structure using AI
	 */
	public migrateGameData(exportData: Export): Observable<MigrationResult> {
		return from(this.performMigration(exportData));
	}

	private async performMigration(
		exportData: Export,
	): Promise<MigrationResult> {
		try {
			if (!exportData.gameData) {
				throw new Error('No game data found in export');
			}

			const gameType = exportData.gameData.type;
			const currentStructure = await this.getGameDataStructure(gameType);

			// Create AI prompt for migration
			const messages: AIMessage[] = [
				{
					role: 'system',
					content:
						'You are a data migration assistant. Convert old game data JSON to match new TypeScript structure. Return only valid JSON, no explanations.',
					type: 'text',
					cache: false,
				},
				{
					role: 'user',
					content: this.createMigrationPrompt(
						exportData.gameData,
						currentStructure,
					),
					type: 'text',
					cache: false,
				},
			];

			// Call AI service for migration
			return this.aiService
				.CreateResponse(messages)
				.pipe(
					map((response: AIAPIResponse) => {
						if (!response.json) {
							throw new Error(
								'AI migration failed - no response',
							);
						}

						// Create new export with migrated data
						const migratedExport: Export = {
							...exportData,
							gameData: response.json,
						};

						// Validate the migrated data
						return this.validateMigratedData(
							migratedExport,
							gameType,
						);
					}),
					catchError((error) => {
						return of({
							success: false,
							error: error.message || 'Migration failed',
						});
					}),
				)
				.toPromise() as Promise<MigrationResult>;
		} catch (error: any) {
			return {
				success: false,
				error: error.message || 'Migration failed',
			};
		}
	}

	private createMigrationPrompt(
		oldGameData: any,
		newStructure: string,
	): string {
		return `I have an old game data JSON that needs to be migrated to a new structure.

Old JSON:
${JSON.stringify(oldGameData, null, 2)}

New TypeScript structure:
${newStructure}

Please convert the old JSON to match the new structure. Rules:
- Try to intelligently map fields that may have been renamed
- Don't fill new fields unless they can be clearly deduced from existing data
- Preserve all data that can be mapped
- Ensure the 'type' field remains: ${oldGameData.type}
- IMPORTANT: If there are any nested GameData objects (objects with a 'type' field), preserve their 'type' field value exactly as it is
- For nested objects that look like GameData (have a 'type' field that is a number), ensure they maintain their structure
- Return only the migrated JSON, no explanation

Return a valid JSON that matches the new structure.`;
	}

	private async validateMigratedData(
		migratedExport: Export,
		gameType: PluginType,
	): Promise<MigrationResult> {
		try {
			// Skip the full validation for now - just return success if we have the basic structure
			if (
				migratedExport.gameData &&
				migratedExport.gameData.type === gameType
			) {
				return {
					success: true,
					migratedExport: migratedExport,
				};
			} else {
				return {
					success: false,
					error: 'Migrated data is missing gameData or has wrong type',
					migratedExport: migratedExport,
				};
			}
		} catch (error: any) {
			return {
				success: false,
				error: `Validation failed: ${error.message}`,
				migratedExport: migratedExport, // Still return it for debugging
			};
		}
	}

	/**
	 * Get TypeScript type definition for a GameData type
	 * In a real implementation, this could be generated or maintained separately
	 */
	private getGameDataTypeDefinition(
		pluginType: PluginType,
		className: string,
	): string {
		// This is a fallback method - all GameData classes should now implement getStructureDefinition()
		return `class ${className} extends GameData {
	type: PluginType = ${pluginType};
	
	// Abstract methods implementation
	GetUsedArticles(): number[];
	GetContainedLocalGameDataObjects(): GameData[];
	
	// Note: This is a generic fallback definition.
	// The specific GameData class should implement getStructureDefinition() method
	// to provide accurate type information for migration.
}`;
	}
}
